import React, { useState, useRef, useCallback, ReactNode } from "react";
import { cn } from "../../styles";
import { LocalizationScope } from "../../util/LocalizationScope";
import { Typography } from "../../atoms";
import { ThemeColor } from "../../props";
import { processErrorContent } from "./helper";
import { BaseFormFieldProps, FormFieldConstraint } from "./index";
import { ButtonControl } from "./ButtonControl";
import { CommonValues, CommonValueOption } from "./CommonValues";

export interface ObjectFieldValue {
  values: Record<string, any>;
  domain?: string;
}

export interface FormFieldObjectProps extends BaseFormFieldProps {
  constraints: FormFieldConstraint;
  value: ObjectFieldValue | null;
  fields?: any[];
  onChangeField: (id: string, value: any, opts?: any) => void;
  commonValues?: CommonValueOption[];
  resolvedCommonValues?: { values: any[]; domain?: string } | null;
  renderValue?: (value: ObjectFieldValue) => ReactNode;
  renderPlaceholder?: () => ReactNode;
  renderDialog: (ctx: {
    open: boolean;
    onClose: () => void;
    onClear: (() => void) | false;
    onSelect: (values: any, domain?: string) => void;
  }) => ReactNode;
}

export const ObjectField: React.FC<FormFieldObjectProps> = ({
  className,
  id,
  value,
  fields = [],
  onChangeField,
  label = "Label",
  localKey,
  errors = [],
  touched,
  helper,
  helperKey,
  constraints,
  disabled,
  keepHeight,
  noLabel,
  dense,
  commonValues,
  resolvedCommonValues,
  renderValue,
  renderPlaceholder,
  renderDialog,
  readOnly,
}) => {
  const [open, setOpen] = useState(false);
  const persistedValue = useRef<ObjectFieldValue | null>(null);

  const error = touched && errors[0];

  const updateValue = useCallback(
    (newValue: ObjectFieldValue | null) => {
      if (newValue) {
        const { values, domain } = newValue;
        onChangeField(id, { values, domain });
        fields.forEach((f) => {
          if (
            f.domain === domain &&
            f.subKey in values &&
            values[f.subKey] !== f.value
          )
            onChangeField(f.id, values[f.subKey]);
        });
      } else {
        onChangeField(id, null);
        fields.forEach((f) => {
          if (f.value) onChangeField(f.id, null);
        });
      }
    },
    [fields, id, onChangeField]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    persistedValue.current = value;
  }, [value]);

  const handleClose = useCallback(() => {
    setOpen(false);
    updateValue(persistedValue.current);
  }, [updateValue]);

  const handleClear = useCallback(() => {
    setOpen(false);
    updateValue(null);
  }, [updateValue]);

  const handleSelect = useCallback(
    (values: any, domain?: string) => {
      setOpen(false);
      updateValue({ values, domain });
    },
    [updateValue]
  );

  const showClear = !readOnly && !constraints.presence && !!value;

  return (
    <div className={className}>
      {!noLabel && (
        <div className="flex">
          <Typography.Label
            htmlFor={id}
            className={cn(
              "block font-medium select-none",
              dense ? "mb-0.5 text-xs" : "mb-1 text-sm"
            )}
            content={label || { local: id }}
          />
          {!!constraints.presence && (
            <span className="text-red-500 ml-0.5 text-xs align-super">*</span>
          )}
        </div>
      )}
      <ButtonControl
        onClick={handleOpen}
        disabled={disabled}
        readOnly={readOnly}
        error={!!error}
        dense={dense}
      >
        <LocalizationScope scope={[localKey, "::PSS::Select"]}>
          {value && renderValue ? (
            <div className={cn("w-full", dense ? "p-1.5" : "p-2")}>
              {renderValue(value)}
            </div>
          ) : !value ? (
            renderPlaceholder ? (
              renderPlaceholder()
            ) : (
              <div
                className={cn(
                  "w-full flex items-center",
                  dense ? "px-2 py-3 text-xs" : "px-2 py-4 text-sm",
                  "text-front-400 dark:text-front-500",
                )}
              >
                <Typography.Span content={{ local: "Placeholder" }} />
              </div>
            )
          ) : (
            <div className={cn(dense ? "p-1.5" : "p-2")} />
          )}
        </LocalizationScope>
      </ButtonControl>
      {(helperKey || helper || keepHeight) && !error && (
        <LocalizationScope scope={localKey}>
          <Typography.P
            id={`${id}-helper`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.gray}
            content={
              value?.domain
                ? { local: value.domain }
                : helperKey
                  ? { local: helperKey }
                  : helper
                    ? helper
                    : null
            }
          >
            {" "}
          </Typography.P>
        </LocalizationScope>
      )}
      {error && (
        <LocalizationScope scope={[localKey, "::PSS"]}>
          <Typography.P
            id={`${id}-error`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.error}
            content={processErrorContent(error)}
          />
        </LocalizationScope>
      )}
      {commonValues && resolvedCommonValues && (
        <CommonValues
          localKey={localKey}
          options={resolvedCommonValues.values.map((v: any) => ({
            values: v,
            domain: resolvedCommonValues.domain,
          }))}
          onSelect={({ values, domain }: any) => handleSelect(values, domain)}
          disabled={disabled}
        />
      )}
      {renderDialog({
        open,
        onClose: handleClose,
        onClear: showClear && handleClear,
        onSelect: handleSelect,
      })}
    </div>
  );
};

ObjectField.displayName = "FormField.Object";

export { ObjectField as Object };
export interface ObjectProps extends FormFieldObjectProps {}
